import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';

import {isNilOrEmpty, isNotNil, isPositive} from 'ramda-adjunct';

import {
  useGetParticipationQuery,
  useGetTireSetCountQuery,
  useGetTireVehicleCountQuery,
} from '@dms/api/core';
import {useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetBusinessCaseQuery, useGetVehicleQuery, useListTestDriveQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetRentalVehicleQuery,
  useGetSaleVehicleByVehicleQuery,
  useGetSaleVehicleQuery,
  useGetVehicleComplaintsQuery,
  useVehicleActiveReservationQuery,
} from '@dms/api/vehicle';
import {useGetVehicleWarehouseManagementSettingsQuery} from '@dms/api/warehouse';
import {
  useGetPostponeJobsByVehicleIdQuery,
  useGetServiceVehicleQuery,
  useGetVehicleServiceCampaignQuery,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {
  ErrorPage,
  NavigationItem,
  OwnershipSection,
  is404ApiError,
  useDocumentContextCount,
  usePricePermissions,
  usePermissions,
  useVehiclePhotosPermissions,
  VehicleWarehouseProvider,
  useChecklist,
  useChecklistBadge,
  useRecordCount,
  PageV2,
  Page,
} from '@dms/shared';
import {VehiclePhotosRoutes} from '@dms/teas';

import {buildArray, buildObject, composePath, useRequiredParams} from 'shared/web';

import {Advertisements} from './(sections)/Advertisements/Advertisements';
import {BusinessCases} from './(sections)/BusinessCases/BusinessCases';
import {Campaigns} from './(sections)/Campaigns/Campaigns';
import {Claims} from './(sections)/Claims/Claims';
import {Customers} from './(sections)/Customers/Customers';
import {Documents} from './(sections)/Documents/Documents';
import {HistoryOfRepairs} from './(sections)/HistoryOfRepairs/HistoryOfRepairs';
import {Insights} from './(sections)/Insights/Insights';
import {Interests} from './(sections)/Interests/Interests';
import {InspectionPhotos} from './(sections)/Photos/InspectionPhotos';
import {VehiclePhotos} from './(sections)/Photos/VehiclePhotos';
import {VehiclePostponedJobs} from './(sections)/PostponedJobs/VehiclePostponedJobs';
import {PricesAndFinances} from './(sections)/PricesAndFinances/PricesAndFinances';
import {Reservations} from './(sections)/Reservations/Reservations';
import {TestDrives} from './(sections)/TestDrives/TestDrives';
import {TestDrivesV2} from './(sections)/TestDrives/TestDrivesV2';
import {ThreeHundredAndSixtyPhotos} from './(sections)/ThreeHundredAndSixtyPhotos/ThreeHundredAndSixtyPhotos';
import {Tires} from './(sections)/Tires/Tires';
import {VehicleActivityLogs} from './(sections)/VehicleActivityLogs/VehicleActivityLogs';
import {VehicleChecklists} from './(sections)/VehicleChecklists/VehicleChecklists';
import {Conditions} from './(sections)/VehicleCondition/Conditions';
import {InspectionDetail} from './(sections)/VehicleCondition/InspectionDetail';
import {Inspections} from './(sections)/VehicleCondition/Inspections';
import {VehicleDetails} from './(sections)/VehicleDetails/VehicleDetails';
import {VehicleWarehouseInfo} from './(sections)/WarehouseInfo/VehicleWarehouseInfo';
import {VehicleHeader} from './components/VehicleHeader';

export function VehicleDetail() {
  const {id} = useRequiredParams();

  const documentsCountBadge = useDocumentContextCount('vehicle', id);
  const {businessCaseCount, interestCount, toMenuBadge} = useRecordCount({
    customerId: undefined,
    vehicleId: id,
  });
  const {count: tasksTodoCount} = useTasksCount({
    entityId: id,
    relation: EntityResourceIds.vehicle,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: id,
    relation: EntityResourceIds.vehicle,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {
    data: vehicleData,
    isLoading: isVehicleLoading,
    isError: isVehicleErrored,
    error: vehicleError,
  } = useGetVehicleQuery({vehicleId: id});

  const {data: allInStockSaleVehicles} = useGetSaleVehicleByVehicleQuery({
    vehicleId: id,
    type: 'IN_STOCK',
  });

  const {data: saleVehicle, isError: isSaleVehicleError} = useGetSaleVehicleQuery({
    vehicleId: id,
  });

  const {data: rentalVehicle, isError: isRentalVehicleError} = useGetRentalVehicleQuery({
    vehicleId: id,
  });

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: id,
  });

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: saleVehicle?.saleBusinessCaseId ?? '',
    },
    {skip: isNilOrEmpty(saleVehicle?.saleBusinessCaseId)}
  );

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: saleVehicle?.saleBusinessCaseId ?? ''},
    {skip: isNilOrEmpty(saleVehicle?.saleBusinessCaseId)}
  );

  const [
    hasComplaintReadPermission,
    hasReadBusinessCasePurchasePermission,
    hasReadBusinessCaseSellingPermission,
    hasVehicleReadActiveReservationPermission,
    hasVehicleReadReservationHistoryPermission,
    hasVehicleReadWarehouseInfoPermission,
    canReadBusinessCase,
    canReadRentalVehicle,
    hasReadAdvertisingSettingsPermission,
    canReadServiceCase,
    canReadDocuments,
    canReadInterest,
    canReadPneuhotel,
    hasReadTestDrivePermission,
  ] = usePermissions({
    permissionKeys: [
      'complaintRead',
      'viewBusinessCasePurchase',
      'viewBusinessCaseSelling',
      'vehicleReadActiveReservation',
      'vehicleReadReservationHistory',
      'vehicleReadWarehouseInformation',
      'businessCaseRead',
      'readRentalVehicle',
      'readAdvertisingSettings',
      'serviceCaseRead',
      'readDocuments',
      'interestRead',
      'pneuhotelRead',
      'readTestDrive',
    ],
    scopes: {
      complaintRead: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      vehicleReadActiveReservation: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleReadReservationHistory: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      viewBusinessCasePurchase: {
        // BC tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // BC tab should be allowed regardless of branch scope
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // BC tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // BC tab should be allowed regardless of branch scope
        branchId: 'SKIP',
      },
      interestRead: {
        // interest tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // interest tab should be allowed regardless of branch scope
        branchId: 'SKIP',
      },
    },
  });

  const isWorkShopEnabled = canReadServiceCase;
  const isTireInventoryEnabled = canReadPneuhotel;

  const {data: serviceVehicle, isError: isServiceVehicleError} = useGetServiceVehicleQuery(
    {vehicleId: id},
    {skip: !isWorkShopEnabled}
  );

  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId: id,
  });

  const {data: vehicleServiceCampaign} = useGetVehicleServiceCampaignQuery(
    {vehicleId: id},
    {skip: !isWorkShopEnabled}
  );
  const {data: postopnedJobsData} = useGetPostponeJobsByVehicleIdQuery(
    {vehicleId: id},
    {skip: !isWorkShopEnabled}
  );
  const {data: vehicleComplaints} = useGetVehicleComplaintsQuery({vehicleId: id});
  const {data: warehouseSettings} = useGetVehicleWarehouseManagementSettingsQuery();
  const {data: vehicleTires} = useGetTireVehicleCountQuery(
    {vehicleId: id},
    {skip: !isTireInventoryEnabled}
  );

  const isSalesModuleEnabled = canReadBusinessCase;
  const isRentalVehicleModuleEnabled = canReadRentalVehicle;

  const {canViewAnyOfVehiclePriceFields: canReadFinances} = usePricePermissions({
    vehicleRecordId: id,
    businessCaseRecordId: saleVehicle?.saleBusinessCaseId,
  });

  const activeServiceCampaignsLength =
    vehicleServiceCampaign?.serviceCampaign?.filter(
      (campaign) => campaign.kind === 'ACTIVE_CAMPAIGN'
    ).length ?? 0;

  const isSaleVehicle = isNotNil(saleVehicle) && !isSaleVehicleError;
  const isServiceVehicle = isNotNil(serviceVehicle) && !isServiceVehicleError;
  const isRentalVehicle = isNotNil(rentalVehicle) && !isRentalVehicleError;

  const {progress: saleVehicleProgress} = useChecklist({
    recordId: saleVehicle?.id || '',
    resourceId: 'SALE_VEHICLE',
  });

  const {progress: serviceVehicleProgress} = useChecklist({
    recordId: serviceVehicle?.serviceVehicleId || '',
    resourceId: 'SERVICE_VEHICLE',
  });

  const {badgeSettings} = useChecklistBadge({
    progress: [saleVehicleProgress, serviceVehicleProgress],
  });

  const {data: vehicleTireSets} = useGetTireSetCountQuery(
    {vehicleId: id ?? '', states: ['INSTOCK']},
    {skip: !isServiceVehicle}
  );

  const {data: vehicleCustomers} = useGetVehicleCustomerQuery(
    {vehicleId: id},
    {skip: !isServiceVehicle}
  );

  const {
    error: vehicleReservationError,
    isError: isVehicleReservationError,
    status: vehicleReservationStatus,
  } = useVehicleActiveReservationQuery({
    vehicleId: id,
  });

  const {data: listTestDrive} = useListTestDriveQuery({
    vehicleId: id,
  });

  const navigationItems = buildArray<NavigationItem>()
    .when(isSaleVehicle, {
      id: 'insights',
      label: i18n.t('entity.interest.labels.overviewTab'),
      href: composePath(vehiclesRoutes.insights, {params: {id}}),
      content: <Insights vehicleId={id} />,
      'data-testid': testIds.vehicles.detail('navigation-item-insights'),
      hasSeparator: true,
    })
    .add({
      id: 'details',
      label: i18n.t('entity.vehicle.labels.vehicleDetails'),
      href: composePath(vehiclesRoutes.detail, {params: {id}}),
      content: [
        {route: vehiclesRoutes.detail, element: <VehicleDetails />},
        {route: vehiclesRoutes.vehicleData, element: <VehicleDetails />},
        {route: vehiclesRoutes.features, element: <VehicleDetails />},
        {route: vehiclesRoutes.serviceIntervals, element: <VehicleDetails />},
        {route: vehiclesRoutes.additionalInformation, element: <VehicleDetails />},
        {route: vehiclesRoutes.rental, element: <VehicleDetails />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-details'),
    })
    .when(isSalesModuleEnabled || isRentalVehicleModuleEnabled || isWorkShopEnabled, {
      id: 'conditions',
      label: i18n.t('entity.vehicle.labels.vehicleCondition'),
      href: composePath(
        isSaleVehicle ? vehiclesRoutes.conditionDetail : vehiclesRoutes.conditionsInspections,
        {
          params: {
            id,
          },
        }
      ),
      content: [
        {route: vehiclesRoutes.conditionsInspections, element: <Inspections />},
        {route: vehiclesRoutes.inspectionDetail, element: <InspectionDetail />},
        {route: vehiclesRoutes.inspectionDetailTab, element: <InspectionDetail />},
        {route: vehiclesRoutes.conditions, element: <Conditions />},
        {route: vehiclesRoutes.conditionDetail, element: <Conditions />},
        {route: vehiclesRoutes.conditionDetailTab, element: <Conditions />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-conditions'),
    })
    .when(
      isFeatureEnabled(featureFlags.SALES_VEHICLE_WAREHOUSE_MANAGEMENT) &&
        isSaleVehicle &&
        warehouseSettings?.isEnabled &&
        hasVehicleReadWarehouseInfoPermission &&
        isSalesModuleEnabled,
      {
        id: 'vehicleWarehouses',
        label: i18n.t('entity.vehicle.labels.vehicleWarehouseInfo'),
        href: composePath(vehiclesRoutes.vehicleWarehouseInfo, {params: {id}}),
        content: [
          {
            route: vehiclesRoutes.vehicleWarehouseInfo,
            element: (
              <VehicleWarehouseInfo
                vehicleId={id}
                data-testid={testIds.vehicles.vehicleWarehouseInfo()}
              />
            ),
          },
        ],
        isDisabled: (allInStockSaleVehicles?.saleVehicleIds ?? []).length < 1,
      }
    )
    .when(
      ((isSaleVehicle && isSalesModuleEnabled) ||
        (isRentalVehicle && isRentalVehicleModuleEnabled)) &&
        canReadFinances,
      {
        id: 'finances',
        label: i18n.t('entity.vehicle.labels.finances'),
        href: composePath(vehiclesRoutes.finances, {params: {id}}),
        content: [
          {route: vehiclesRoutes.finances, element: <PricesAndFinances />},
          {route: vehiclesRoutes.financesPrices, element: <PricesAndFinances />},
          {route: vehiclesRoutes.financesEarningsCosts, element: <PricesAndFinances />},
          {route: vehiclesRoutes.financesRentalPrices, element: <PricesAndFinances />},
        ],
        'data-testid': testIds.vehicles.detail('navigation-item-finances'),
      }
    )
    .when(isSalesModuleEnabled && isSaleVehicle && hasReadAdvertisingSettingsPermission, {
      id: 'advertisements',
      label: i18n.t('entity.vehicle.labels.advertisements'),
      href: composePath(vehiclesRoutes.publishing, {params: {id}}),
      content: [{route: vehiclesRoutes.publishing, element: <Advertisements />}],
      'data-testid': testIds.vehicles.detail('navigation-item-advertisements'),
    })
    .when(isSalesModuleEnabled || isRentalVehicleModuleEnabled || isWorkShopEnabled, {
      id: 'photos',
      label: i18n.t('entity.photo.labels.photos'),
      href: composePath(vehiclesRoutes.photos, {
        params: {
          id,
          tab:
            canReadVehicleAlbumPhotos && isSaleVehicle
              ? VehiclePhotosRoutes.vehiclePhotos
              : VehiclePhotosRoutes.condition,
        },
      }),
      content: [
        {route: vehiclesRoutes.photos, element: <VehiclePhotos />},
        {route: vehiclesRoutes.photoByInspectionId, element: <InspectionPhotos />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-photos'),
      hasSeparator:
        !canReadDocuments && !isFeatureEnabled(featureFlags.VEHICLE_THREE_HUNDRED_AND_SIXTY_PHOTOS),
    })
    .whenFeatureEnabled(featureFlags.VEHICLE_THREE_HUNDRED_AND_SIXTY_PHOTOS, {
      id: 'threeHundredAndSixtyPhotos',
      label: i18n.t('entity.vehicle.labels.threeHundredAndSixtyPhotos'),
      href: composePath(vehiclesRoutes.threeHundredAndSixtyPhotos, {params: {id}}),
      content: <ThreeHundredAndSixtyPhotos />,
      'data-testid': testIds.vehicles.detail('navigation-item-threeHundredAndSixtyPhotos'),
      hasSeparator: !canReadDocuments,
    })
    .when(canReadDocuments, {
      id: 'documents',
      label: i18n.t('page.vehicle.documents.title'),
      href: composePath(vehiclesRoutes.documents, {params: {id}}),
      content: <Documents />,
      badge: documentsCountBadge,
      'data-testid': testIds.vehicles.detail('navigation-item-documents'),
      hasSeparator: true,
    })
    .whenFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT, {
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(vehiclesRoutes.tasks, {params: {id}}),
      'data-testid': testIds.vehicles.detail('navigation-item-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          gridCode="vehicle-task"
          entityId={id}
          data-testid={testIds.vehicles.detail('tasks')}
        />
      ),
      hasSeparator: !isFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'checklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(vehiclesRoutes.checklists, {params: {id}}),
      content: (
        <VehicleChecklists
          saleVehicleId={saleVehicle?.id || ''}
          serviceVehicleId={serviceVehicle?.serviceVehicleId || ''}
          vehicleId={id}
        />
      ),
      badge: badgeSettings,
      'data-testid': testIds.vehicles.detail('navigation-item-checklists'),
      hasSeparator: true,
    })
    .when(isSaleVehicle, {
      id: 'customers',
      label: i18n.t('page.customers.title'),
      href: composePath(vehiclesRoutes.customers, {params: {id}}),
      content: <Customers />,
      'data-testid': testIds.vehicles.detail('navigation-item-customers'),
    })
    .when(isSaleVehicle && canReadInterest && isSalesModuleEnabled, {
      id: 'interests',
      label: i18n.t('page.interests.title'),
      href: composePath(vehiclesRoutes.interests, {params: {id}}),
      content: <Interests />,
      'data-testid': testIds.vehicles.detail('navigation-item-interests'),
      badge: toMenuBadge(interestCount?.count ?? 0),
    })
    .when(isSaleVehicle && isSalesModuleEnabled, {
      id: 'test-drives',
      label: i18n.t('entity.testDrive.labels.testDrives'),
      href: composePath(vehiclesRoutes.testDrives, {params: {id}}),
      content: isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2) ? (
        <TestDrivesV2 />
      ) : (
        <TestDrives />
      ),
      isDisabled:
        (isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2) && !hasReadTestDrivePermission) ||
        false,
      'data-testid': testIds.vehicles.detail('navigation-item-test-drive'),
      badge: toMenuBadge(listTestDrive?.length ?? 0),
    })
    .when(
      isSaleVehicle &&
        hasVehicleReadActiveReservationPermission &&
        hasVehicleReadReservationHistoryPermission &&
        isSalesModuleEnabled,
      {
        id: 'reservations',
        label: i18n.t('entity.vehicle.labels.reservations'),
        href: composePath(vehiclesRoutes.reservations, {params: {id}}),
        content: <Reservations />,
        'data-testid': testIds.vehicles.detail('navigation-item-reservations'),
        badge:
          vehicleReservationStatus !== 'pending' &&
          !(isVehicleReservationError && is404ApiError(vehicleReservationError))
            ? {
                icon: 'navigation/check',
                colorScheme: 'green',
              }
            : undefined,
      }
    )
    .when(
      isSalesModuleEnabled &&
        isSaleVehicle &&
        saleVehicle?.id &&
        (hasReadBusinessCasePurchasePermission || hasReadBusinessCaseSellingPermission),
      {
        id: 'business-cases',
        label: i18n.t('entity.vehicle.labels.businessCases'),
        href: composePath(vehiclesRoutes.businessCases, {params: {id}}),
        content: <BusinessCases />,
        badge: toMenuBadge(businessCaseCount?.count ?? 0),
        'data-testid': testIds.vehicles.detail('navigation-item-business-cases'),
      }
    )
    .when(
      isFeatureEnabled(featureFlags.SALES_CLAIMS) &&
        hasComplaintReadPermission &&
        isSaleVehicle &&
        isSalesModuleEnabled,
      {
        id: 'vehicle-complaints',
        label: i18n.t('entity.vehicle.labels.complaints'),
        href: composePath(vehiclesRoutes.complaints, {params: {id}}),
        content: <Claims />,
        badge: isPositive(vehicleComplaints?.length)
          ? {
              value: vehicleComplaints?.length ?? 0,
              colorScheme: 'neutral',
            }
          : undefined,
        'data-testid': testIds.vehicles.detail('complaints'),
        hasSeparator: !isWorkShopEnabled,
      }
    )
    .when(
      isWorkShopEnabled && isServiceVehicle,
      buildObject<NavigationItem>()
        .id('postponed-jobs')
        .label(i18n.t('entity.vehicle.labels.postponedJobs'))
        .href(composePath(vehiclesRoutes.postponedJobs, {params: {id}}))
        .content(<VehiclePostponedJobs />)
        .badge(
          {value: String(postopnedJobsData?.count ?? ''), colorScheme: 'neutral'},
          Boolean(postopnedJobsData?.count)
        )
        .build()
    )
    .when(isWorkShopEnabled && isServiceVehicle, {
      id: 'ownership',
      label: i18n.t('entity.serviceCase.labels.ownership'),
      href: composePath(vehiclesRoutes.ownership, {params: {id}}),
      content: <OwnershipSection vehicleId={id} />,
      badge: toMenuBadge(vehicleCustomers?.length ?? 0),
      'data-testid': testIds.vehicles.detail('navigation-item-ownership'),
    })
    .when(isWorkShopEnabled && isServiceVehicle, {
      id: 'historyOfRepairs',
      label: i18n.t('entity.serviceCase.labels.historyOfRepairs'),
      href: composePath(vehiclesRoutes.historyOfRepairs, {params: {id}}),
      content: [
        {
          route: vehiclesRoutes.historyOfRepairs,
          element: <HistoryOfRepairs vehicleId={id} />,
        },
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-historyOfRepairs'),
    })
    .when(isWorkShopEnabled && isServiceVehicle, {
      id: 'serviceCampaigns',
      label: i18n.t('entity.campaigns.labels.serviceCampaigns'),
      href: composePath(vehiclesRoutes.serviceCampaigns, {params: {id}}),
      badge: {value: activeServiceCampaignsLength, colorScheme: 'neutral'},
      content: <Campaigns vehicleId={id} />,
      'data-testid': testIds.vehicles.detail('navigation-item-serviceCampaigns'),
    })
    .when(isTireInventoryEnabled, {
      id: 'tires',
      label: i18n.t('page.tiresInventory.labels.tires'),
      href: composePath(vehiclesRoutes.tires, {params: {id}}),
      content: <Tires />,
      badge: isPositive(vehicleTireSets?.count)
        ? {value: String(vehicleTireSets?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      isDisabled: !Boolean(vehicleTires?.count),
      'data-testid': testIds.vehicles.detail('navigation-item-tires'),
      hasSeparator: true,
    })
    .whenFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS, {
      id: 'activityLogs',
      label: i18n.t('general.labels.activityLogs'),
      href: composePath(vehiclesRoutes.activityLog, {params: {id}}),
      content: [
        {route: vehiclesRoutes.activityLog, element: <VehicleActivityLogs />},
        {route: vehiclesRoutes.emailActivityLogs, element: <VehicleActivityLogs />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-activityLogs'),
    });

  if (!isVehicleLoading && is404ApiError(vehicleError)) {
    return <ErrorPage message={i18n.t('page.vehicle.notifications.notFound')} />;
  }

  const PageComponent = isFeatureEnabled(featureFlags.ENTITY_HEADER_V2) ? PageV2 : Page;

  return (
    <DataStatus isLoading={isVehicleLoading} isError={isVehicleErrored} minHeight={100}>
      {isNotNil(vehicleData) && !isVehicleLoading ? (
        <VehicleWarehouseProvider>
          <PageComponent
            key={id}
            navigation={navigationItems}
            header={<VehicleHeader vehicleId={id} data-testid={testIds.vehicles.detail()} />}
            data-testid={testIds.vehicles.detail()}
          />
        </VehicleWarehouseProvider>
      ) : null}
    </DataStatus>
  );
}
